import { AgreeToTermsPolicy, experience, ExtraParamsKey, SignInMode } from '@logto/schemas';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';

import LandingPageLayout from '@/Layout/LandingPageLayout';
import SingleSignOnFormModeContextProvider from '@/Providers/SingleSignOnFormModeContextProvider';
import SingleSignOnFormModeContext from '@/Providers/SingleSignOnFormModeContextProvider/SingleSignOnFormModeContext';
import WebAuthnContextProvider from '@/Providers/WebAuthnContextProvider';
import PasskeySignInButton from '@/components/Button/PasskeySignInButton';
import SsoSignInButton from '@/components/Button/SsoSignInButton';
import Divider from '@/components/Divider';
import GoogleOneTap from '@/components/GoogleOneTap';
import TextLink from '@/components/TextLink';
import SocialSignInList from '@/containers/SocialSignInList';
import TermsAndPrivacyCheckbox from '@/containers/TermsAndPrivacyCheckbox';
import TermsAndPrivacyLinks from '@/containers/TermsAndPrivacyLinks';
import useBrandName from '@/hooks/use-brand-name';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import { useSieMethods } from '@/hooks/use-sie';
import type { SignInExperienceResponse } from '@/types';
import useTerms from '@/hooks/use-terms';
import DynamicT from '@/shared/components/DynamicT';

import ErrorPage from '../ErrorPage';

import Main from './Main';
import styles from './index.module.scss';

/**
 * TDA: everything that is "continue with …", lifted out of the footer to sit
 * ABOVE the credential form, with the `or` divider between. Plane's shape.
 *
 * ⚠️ Passkey belongs HERE, not in the footer where upstream puts it. A passkey
 * is a way to sign in, exactly like Google or an SSO provider — leaving it below
 * the Sign in button made it read as an afterthought, and on a tenant with
 * passkey but no social connectors it stranded the button under the form with
 * no divider anywhere.
 *
 * ⚠️ The divider is keyed on "is there anything above me", not on social alone.
 * Keying it on social was the bug: passkey-only tenants got a button and a form
 * with nothing separating them.
 */
const SignInMethodsBlock = ({
  signInMethods,
  socialConnectors,
  passkeySignIn,
}: {
  readonly signInMethods: SignInExperienceResponse['signIn']['methods'];
  readonly socialConnectors: SignInExperienceResponse['socialConnectors'];
  readonly passkeySignIn: SignInExperienceResponse['passkeySignIn'] | undefined;
}) => {
  const showPasskey = Boolean(passkeySignIn?.enabled && passkeySignIn.showPasskeyButton);
  const hasSocial = socialConnectors.length > 0;

  /* Nothing to put above the form, or no form to separate from — either way the
     divider would be a rule with one side empty. */
  if (signInMethods.length === 0 || (!hasSocial && !showPasskey)) {
    return null;
  }

  return (
    <>
      {hasSocial && <SocialSignInList className={styles.main} socialConnectors={socialConnectors} />}
      {/*
        * ⚠️ Wrapped, and the wrapper is what carries the spacing.
        *
        * Measured: a social button gets `margin: 0 0 16px` from
        * `SocialSignInList`'s own item styling, while `PasskeySignInButton`
        * takes no `className` and so sat flush against the divider — every other
        * box on the page matched, only the 16px gap was missing.
        *
        * `.otherMethods` already carries exactly that margin and was declared
        * but unused, so reusing it means `index.module.scss` stays untouched —
        * one fewer upstream file in the merge ledger for a one-line gap.
        */}
      {showPasskey && (
        <div className={styles.otherMethods}>
          <PasskeySignInButton />
        </div>
      )}
      <Divider className={styles.divider} label="description.or" />
    </>
  );
};

const SignInFooters = () => {
  const { t } = useTranslation();
  const { termsValidation, agreeToTermsPolicy } = useTerms();
  const navigate = useNavigateWithPreservedSearchParams();

  const {
    signInMethods,
    signUpMethods,
    socialConnectors,
    signInMode,
    singleSignOnEnabled,
    passkeySignIn,
  } = useSieMethods();

  /*
   * TDA: is SSO the only way in?
   *
   * When it is, it becomes the card's primary button (the mock's shape) instead
   * of upstream's footer text link — there is no form for it to defer to. When
   * it is not, the link is still right: the form's own submit is the primary
   * action, and a second full-width button beside it gives the eye no first move.
   *
   * ⚠️ Passkey counts. It is a way to sign in like any other and renders its own
   * button above the form, so an SSO + passkey tenant has two entries and neither
   * should outrank the other.
   */
  const isSsoOnlyEntry =
    signInMethods.length === 0 &&
    socialConnectors.length === 0 &&
    !(passkeySignIn?.enabled && passkeySignIn.showPasskeyButton);

  const { showSingleSignOnForm } = useContext(SingleSignOnFormModeContext);

  const handleSsoNavigation = useCallback(async () => {
    /**
     * Check if the user has agreed to the terms and privacy policy before navigating to the SSO page
     * when the policy is set to `Manual`
     */
    if (agreeToTermsPolicy === AgreeToTermsPolicy.Manual && !(await termsValidation())) {
      return;
    }
    navigate('/single-sign-on/email');
  }, [agreeToTermsPolicy, navigate, termsValidation]);

  /* Hide footers when showing Single Sign On form */
  if (showSingleSignOnForm) {
    return null;
  }

  return (
    <>
      {
        // Single Sign On footer
        singleSignOnEnabled && (
          <>
            {isSsoOnlyEntry ? (
              <>
                <SsoSignInButton onClick={handleSsoNavigation} />
                {/* TDA: the mock's closing line, above a rule. It says why there
                    is only one button, which is the question a card with a single
                    action invites. */}
                <div className={styles.ssoFootnote}>
                  <DynamicT forKey="description.tdx_sso_footnote" />
                </div>
              </>
            ) : (
              <div className={styles.singleSignOn}>
                {t('description.use')}{' '}
                <TextLink text="action.single_sign_on" onClick={handleSsoNavigation} />
              </div>
            )}
            {
              /**
               * If only SSO sign-in methods are available, display the agreement checkbox when the agreement policy is `Manual`.
               */
              signInMethods.length === 0 &&
                socialConnectors.length === 0 &&
                agreeToTermsPolicy === AgreeToTermsPolicy.Manual && (
                  <TermsAndPrivacyCheckbox className={styles.checkboxForSsoOnly} />
                )
            }
          </>
        )
      }
      {
        // Create Account footer
        signInMode === SignInMode.SignInAndRegister && signUpMethods.length > 0 && (
          <div className={styles.createAccount}>
            {t('description.no_account')}{' '}
            <TextLink replace to="/register" text="action.create_account" />
          </div>
        )
      }
    </>
  );
};

const SignIn = () => {
  const { signInMethods, socialConnectors, signInMode, passkeySignIn } = useSieMethods();
  const { agreeToTermsPolicy } = useTerms();
  const [params] = useSearchParams();
  const brandName = useBrandName();

  if (!signInMode) {
    return <ErrorPage />;
  }

  if (signInMode === SignInMode.Register) {
    return <Navigate to="/register" />;
  }

  if (params.get(ExtraParamsKey.OneTimeToken)) {
    return (
      <Navigate
        replace
        to={{ pathname: `/${experience.routes.oneTimeToken}`, search: `?${params.toString()}` }}
      />
    );
  }

  return (
    <LandingPageLayout
      title="description.sign_in_to_your_account"
      titleInterpolation={{ brandName }}
    >
      <GoogleOneTap context="signin" />
      <WebAuthnContextProvider>
        <SingleSignOnFormModeContextProvider>
          {/* TDA: Plane puts the social/SSO buttons ABOVE the credential form,
              with the `or` divider between them. Upstream renders them below, as
              part of the footer. Measured on plane.tdadevelop.com. */}
          <SignInMethodsBlock
            signInMethods={signInMethods}
            socialConnectors={socialConnectors}
            passkeySignIn={passkeySignIn}
          />
          <Main signInMethods={signInMethods} socialConnectors={socialConnectors} />
          <SignInFooters />
        </SingleSignOnFormModeContextProvider>
      </WebAuthnContextProvider>
      {
        // Only show terms and privacy links for sign in page if the agree to terms policy is `Automatic` or `ManualRegistrationOnly`
        agreeToTermsPolicy !== AgreeToTermsPolicy.Manual && (
          <TermsAndPrivacyLinks className={styles.terms} />
        )
      }
    </LandingPageLayout>
  );
};

export default SignIn;
